'use client';

import { withStoreCampaign } from '@/lib/store-attribution';
import { clenoirAppStoreUrl } from '@/lib/site';
import { usePwaInstall } from '@/components/pwa/PwaInstallProvider';
import Link from 'next/link';
import { useState } from 'react';
import type { SVGProps } from 'react';

function AppleIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      aria-hidden
      fill="currentColor"
      height={28}
      viewBox="0 0 24 24"
      width={28}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path d="M16.37 12.74c.03 3.22 2.82 4.29 2.85 4.3-.02.08-.45 1.52-1.49 3-.9 1.28-1.83 2.56-3.3 2.59-1.44.03-1.91-.85-3.56-.85-1.65 0-2.16.83-3.53.88-1.42.05-2.5-1.39-3.4-2.67-1.85-2.63-3.27-7.43-1.37-10.67.94-1.61 2.63-2.63 4.46-2.66 1.39-.03 2.71.93 3.56.93.85 0 2.45-1.15 4.13-.98.7.03 2.69.28 3.96 2.13-.1.06-2.37 1.38-2.34 4.12ZM13.82 4.64c.76-.92 1.27-2.2 1.13-3.48-1.1.04-2.42.73-3.2 1.64-.7.82-1.32 2.13-1.15 3.39 1.22.09 2.46-.62 3.22-1.55Z" />
    </svg>
  );
}

function AndroidIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      aria-hidden
      fill="currentColor"
      height={28}
      viewBox="0 0 24 24"
      width={28}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path d="M17.6 10.81c-.56 0-1.02-.46-1.02-1.02 0-.56.46-1.02 1.02-1.02.56 0 1.02.46 1.02 1.02-.01.56-.46 1.02-1.02 1.02m-11.2 0c-.56 0-1.02-.46-1.02-1.02 0-.56.46-1.02 1.02-1.02s1.02.46 1.02 1.02c0 .56-.46 1.02-1.02 1.02m11.57-6.12 2.03-3.51c.12-.2.05-.46-.15-.58a.43.43 0 0 0-.58.15l-2.06 3.56a12.6 12.6 0 0 0-10.42 0L4.73.75c-.12-.2-.38-.27-.58-.15a.43.43 0 0 0-.15.58L6.03 4.7C2.95 6.34 1 9.33 1 12.82h22c0-3.49-1.95-6.48-5.03-8.13Z" />
    </svg>
  );
}

function BrowserIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      aria-hidden
      fill="none"
      height={28}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.6}
      viewBox="0 0 24 24"
      width={28}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15 15 0 0 1 0 20" />
      <path d="M12 2a15 15 0 0 0 0 20" />
    </svg>
  );
}

export default function AccountPwaInstallPanel() {
  const {
    applyUpdate,
    canInstall,
    canInstallManually,
    install,
    isInstalled,
    isUpdating,
    updateAvailable,
  } = usePwaInstall();
  const [isPending, setIsPending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function handleInstall() {
    setIsPending(true);
    setMessage(null);

    try {
      const outcome = await install();

      if (outcome === 'dismissed') {
        setMessage('Install dismissed.');
      } else if (outcome === 'unavailable') {
        setMessage('Use the browser install menu.');
      }
    } finally {
      setIsPending(false);
    }
  }

  const webStatusLabel = updateAvailable
    ? 'Update ready'
    : isInstalled
      ? 'Installed'
      : canInstall || canInstallManually
        ? 'Available'
        : 'Browser';

  return (
    <div className="account-tab-stack">
      <section className="account-tab-section">
        <div className="account-section-heading">
          <h3>Apps</h3>
        </div>

        <div className="account-product-list">
          <article className="account-product-row">
            <div className="account-product-identity">
              <div className="account-product-icon-shell account-product-icon-shell-web">
                <BrowserIcon />
              </div>
              <div className="account-product-copy">
                <strong>Cleanor Web</strong>
                {message ? <p>{message}</p> : null}
                <div className="account-chip-row">
                  <span className="account-inline-meta">{webStatusLabel}</span>
                </div>
              </div>
            </div>

            <div className="account-product-actions">
              {updateAvailable ? (
                <button
                  className="button account-product-button"
                  disabled={isUpdating}
                  onClick={() => void applyUpdate()}
                  type="button"
                >
                  {isUpdating ? 'Refreshing…' : 'Refresh'}
                </button>
              ) : null}
              {!updateAvailable && canInstall ? (
                <button
                  className="button account-product-button"
                  disabled={isPending}
                  onClick={() => void handleInstall()}
                  type="button"
                >
                  {isPending ? 'Opening…' : 'Install'}
                </button>
              ) : null}
              <Link className="button button-ghost-light account-product-button" href="/tools">
                Open tools
              </Link>
            </div>
          </article>

          <article className="account-product-row">
            <div className="account-product-identity">
              <div className="account-product-icon-shell account-product-icon-shell-ios">
                <AppleIcon />
              </div>
              <div className="account-product-copy">
                <strong>Cleanor for iPhone</strong>
                <div className="account-chip-row">
                  <span className="account-inline-meta">iOS</span>
                </div>
              </div>
            </div>

            <div className="account-product-actions">
              <Link
                className="button button-ghost-light account-product-button"
                href="/cleanor-ios"
              >
                Details
              </Link>
              <a
                className="button account-product-button"
                href={withStoreCampaign(clenoirAppStoreUrl, 'web-account')}
                rel="noreferrer"
                target="_blank"
              >
                App Store
              </a>
            </div>
          </article>

          <article className="account-product-row">
            <div className="account-product-identity">
              <div className="account-product-icon-shell account-product-icon-shell-android">
                <AndroidIcon />
              </div>
              <div className="account-product-copy">
                <strong>Cleanor for Android</strong>
                <div className="account-chip-row">
                  <span className="account-inline-meta">In review</span>
                </div>
              </div>
            </div>

            <div className="account-product-actions">
              <Link className="button button-ghost-light account-product-button" href="/cleanor">
                Details
              </Link>
              <span className="button button-disabled account-product-button">Soon</span>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}
